import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../../context/AuthContext';
import { 
  Globe, 
  ExternalLink, 
  Edit2, 
  Crown, 
  Link, 
  Check, 
  Loader2,
  Star,
  Instagram,
  Facebook,
  Youtube,
  MessageCircle,
  Music2,
  X
} from 'lucide-react';
import ProFeatureButton from '../../../components/ProFeatureButton';
import { useError } from '../../../context/ErrorContext';
import { isPro as checkIsPro } from '../../../utils/planPermissions';
import { baseApi, BaseApiError } from '../../../api/baseClient';

interface QRCodePanelProps {
  businessId: string;
  socialUrl: string;
  onSocialUrlChange: (url: string) => void;
}

interface SocialOption {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;          // solo para el fgColor del QR (siempre sobre blanco)
  iconClass: string;      // color de icono/texto en UI, con variante dark
  selectedClass: string;  // borde+ring del botón seleccionado, con variante dark
  placeholderUrl: string;
  callToAction: string;
  hoverBgColor: string;
}

const IntegratedQRCodePanel: React.FC<QRCodePanelProps> = ({ businessId, socialUrl, onSocialUrlChange }) => {
  const { currentUser } = useAuth();
  const { addError, addValidationError, clearErrors } = useError();
  
  const [isEditingSocial, setIsEditingSocial] = useState<boolean>(false);
  const [localSocialUrl, setLocalSocialUrl] = useState<string>(socialUrl || '');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  
  const [selectedSocial, setSelectedSocial] = useState<string>('custom');

  
  const socialOptions: SocialOption[] = [
    {
      id: 'google',
      name: 'Google Opiniones',
      icon: <Star className="h-5 w-5" />,
      color: '#DB4437',
      iconClass: 'text-red-500 dark:text-red-400',
      selectedClass: 'border-red-500 ring-red-500',
      placeholderUrl: 'https://g.page/r/your-business-id/review',
      callToAction: 'Valora tu experiencia ⭐⭐⭐⭐⭐',
      hoverBgColor: 'hover:bg-red-50 dark:hover:bg-red-500/10'
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: <Instagram className="h-5 w-5" />,
      color: '#E1306C',
      iconClass: 'text-pink-600 dark:text-pink-400',
      selectedClass: 'border-pink-500 ring-pink-500',
      placeholderUrl: 'https://instagram.com/tunegocio',
      callToAction: 'Síguenos en Instagram',
      hoverBgColor: 'hover:bg-pink-50 dark:hover:bg-pink-500/10'
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: <Facebook className="h-5 w-5" />,
      color: '#1877F2',
      iconClass: 'text-blue-600 dark:text-blue-400',
      selectedClass: 'border-blue-500 ring-blue-500',
      placeholderUrl: 'https://facebook.com/tunegocio',
      callToAction: 'Dale like en Facebook',
      hoverBgColor: 'hover:bg-blue-50 dark:hover:bg-blue-500/10'
    },
    {
      id: 'youtube',
      name: 'Youtube',
      icon: <Youtube className="h-5 w-5" />,
      color: '#FF0000',
      iconClass: 'text-red-600 dark:text-red-400',
      selectedClass: 'border-red-500 ring-red-500',
      placeholderUrl: 'https://youtube.com/@tunegocio',
      callToAction: 'Suscríbete a nuestro canal',
      hoverBgColor: 'hover:bg-red-50 dark:hover:bg-red-500/10'
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: <Music2 className="h-5 w-5" />,
      color: '#000000',
      iconClass: 'text-content',
      selectedClass: 'border-content ring-content',
      placeholderUrl: 'https://tiktok.com/@tunegocio',
      callToAction: 'Síguenos en TikTok',
      hoverBgColor: 'hover:bg-surface-2'
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: <MessageCircle className="h-5 w-5" />,
      color: '#25D366',
      iconClass: 'text-green-600 dark:text-green-400',
      selectedClass: 'border-green-500 ring-green-500',
      placeholderUrl: 'https://wa.me/5216861234567',
      callToAction: 'Escríbenos por WhatsApp',
      hoverBgColor: 'hover:bg-green-50 dark:hover:bg-green-500/10'
    },
    {
      id: 'custom',
      name: 'URL Personalizada',
      icon: <Link className="h-5 w-5" />,
      color: '#4B5563',
      iconClass: 'text-content-2',
      selectedClass: 'border-content-3 ring-content-3',
      placeholderUrl: 'https://tunegocio.com',
      callToAction: 'Visita nuestro sitio',
      hoverBgColor: 'hover:bg-surface-2'
    },
    // PRO puede apagar el QR del final por completo (24-ago-2026, pedido de Marrieto: hacen
    // sus cambios desde la computadora y no quieren promoción en el kiosko). Se persiste como
    // socialType 'none' + URL vacía; el kiosko cierra directo tras el feedback.
    {
      id: 'none',
      name: 'Sin QR al final',
      icon: <X className="h-5 w-5" />,
      color: '#6B7280',
      iconClass: 'text-content-3',
      selectedClass: 'border-content-3 ring-content-3',
      placeholderUrl: '',
      callToAction: 'No se muestra nada al terminar',
      hoverBgColor: 'hover:bg-surface-2'
    }
  ];
  
  useEffect(() => {
    if (socialUrl) {
      if (socialUrl.includes('instagram.com')) {
        setSelectedSocial('instagram');
      } else if (socialUrl.includes('facebook.com')) {
        setSelectedSocial('facebook');
      } else if (socialUrl.includes('youtube.com')) {
        setSelectedSocial('youtube');
      } else if (socialUrl.includes('tiktok.com')) {
        setSelectedSocial('tiktok');
      } else if (socialUrl.includes('wa.me') || socialUrl.includes('whatsapp.com')) {
        setSelectedSocial('whatsapp');
      } else if (socialUrl.includes('g.page') || socialUrl.includes('google.com/maps')) {
        setSelectedSocial('google');
      } else {
        setSelectedSocial('custom');
      }
    } else {
      // Sin URL guardada: puede ser «nunca configuró» (custom) o «lo apagó» (none persistido).
      setSelectedSocial(currentUser?.socialType === 'none' ? 'none' : 'custom');
    }
  }, [socialUrl, currentUser?.socialType]);

  useEffect(() => {
    setLocalSocialUrl(socialUrl || '');
  }, [socialUrl]);
  
  // ✅ Usar helper centralizado — premium también pasa
  const hasProAccess = checkIsPro(currentUser?.subscription);

  // QR predeterminado del plan FREE = promoción de Lealify (lealify.com); al pagar PRO el
  // negocio personaliza el segundo QR con su propia URL.
  // ⚠️ 24-jul-2026: revertido a lealify.com. Entre el 25-jun y hoy este QR apuntó al kiosco
  // self-service (`https://<slug>.lealify.com/l/<slug>/<branch>`), pero ese subdominio no
  // tiene vhost (404 + cert ajeno), el gate `tenant_settings.self_service` no se activa en
  // altas nuevas (403) y el flujo es SOLO CONSULTA — no registra visitas, pese al copy.
  // Detalle: memoria `forobeta_lealify_selfservice_qr_gap`.
  const lealifyUrl = 'https://lealify.com';
  
  const currentSocial =
    socialOptions.find(option => option.id === selectedSocial) ||
    socialOptions.find(option => option.id === 'custom')!;

  const handleLocalSave = async () => {
    if (!businessId) {
      addValidationError('El ID de negocio es requerido', 'qr-code-business-id');
      return;
    }
    
    clearErrors();
    setIsSaving(true);

    // «Sin QR al final» no lleva URL: se persiste el tipo con URL vacía.
    let formattedUrl = selectedSocial === 'none' ? '' : localSocialUrl.trim();
    // WhatsApp: aceptar que escriban solo el teléfono (con o sin +, espacios o guiones).
    if (selectedSocial === 'whatsapp' && /^[\d\s()+-]+$/.test(formattedUrl)) {
      formattedUrl = `https://wa.me/${formattedUrl.replace(/\D/g, '')}`;
    }
    if (formattedUrl && !formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = `https://${formattedUrl}`;
    }

    try {
      // Persistir en base: se guarda como tenant_settings.social {socialUrl, socialType}.
      // GET /loyalty/settings lo relee (routes/loyalty.js → social.socialUrl/socialType),
      // así el QR PRO sobrevive a recargar. Antes solo vivía en estado local.
      await baseApi.put('/biz/settings', {
        settings: { social: { socialUrl: formattedUrl, socialType: selectedSocial } },
      });
      onSocialUrlChange(formattedUrl);
      setLocalSocialUrl(formattedUrl);
      setIsEditingSocial(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving social QR URL:', error);
      // El backend ya manda un mensaje útil (403 de permisos, 500 = "Error interno" sin filtrar SQL).
      // Tragárselo dejaba todo fallo con el mismo texto genérico e ilegible para diagnosticar.
      const detail = error instanceof BaseApiError ? error.message : '';
      addError(
        detail
          ? `No se pudo guardar la URL del código QR: ${detail}`
          : 'No se pudo guardar la URL del código QR. Inténtalo de nuevo.',
        'unknown',
        'qr-code-save'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-surface overflow-hidden shadow rounded-lg divide-y divide-line">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium text-content">
          Códigos QR Para Tu Negocio
        </h3>
        <p className="mt-1 text-sm text-content-3">
          Para imprimir o pegar en el mostrador: uno lleva a tu sitio o red social; el otro presenta Lealify a quien lo escanee.
        </p>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Enhanced Social QR (featured for PRO, call-to-action for FREE) */}
          <div className={`bg-surface shadow rounded-lg overflow-hidden ${hasProAccess ? 'border-2 border-accent/40' : ''}`}>
            <div className="px-4 py-3 sm:px-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-500/10 dark:to-purple-500/10">
              <div className="flex justify-between items-center">
                <h3 className="text-base font-medium text-content flex items-center">
                  <ExternalLink className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                  Código QR Personalizado
                  {hasProAccess && <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-300 px-2 py-0.5 rounded">PLAN PRO</span>}
                </h3>
                {hasProAccess && !isEditingSocial && (
                  <button
                    onClick={() => {
                      clearErrors();
                      setIsEditingSocial(true);
                    }}
                    className="inline-flex items-center px-2 py-1 border border-line text-xs font-medium rounded-md shadow-sm text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                  >
                    <Edit2 className="h-3 w-3 mr-1" />
                    Editar
                  </button>
                )}
              </div>
            </div>
            <div className="px-4 py-5 sm:p-6">
              {hasProAccess ? (
                <div className="flex flex-col">
                  {isEditingSocial ? (
                    <div className="mb-4">
                      {/* Social Network Selection */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-content-2 mb-2">
                          Selecciona red social o sitio web
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {socialOptions.map((option) => (
                            <button
                              key={option.id}
                              onClick={() => {
                                setSelectedSocial(option.id);
                                if (option.id !== 'custom') {
                                  setLocalSocialUrl(option.placeholderUrl);
                                }
                              }}
                              className={`py-2 px-3 border rounded-md flex flex-col items-center justify-center transition-colors ${
                                selectedSocial === option.id
                                  ? `${option.selectedClass} ring-1 bg-surface-2`
                                  : 'border-line'
                              } ${option.hoverBgColor}`}
                            >
                              <div className={option.iconClass}>{option.icon}</div>
                              <span className="text-xs mt-1 text-content-2">{option.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* URL Input Field — «Sin QR al final» no lleva URL: solo el botón Guardar */}
                      {selectedSocial !== 'none' && (
                      <label htmlFor="socialUrl" className="block text-sm font-medium text-content-2 mb-1">
                        URL de {currentSocial.name}
                      </label>
                      )}
                      <div className="flex">
                        {selectedSocial === 'none' ? (
                          <p className="flex-grow self-center text-sm text-content-2">
                            Al terminar el feedback, el kiosko cierra sin mostrar ningún QR.
                          </p>
                        ) : (
                        <div className="relative flex-grow">
                          <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${currentSocial.iconClass}`}>
                            {currentSocial.icon}
                          </div>
                          <input
                            type="text"
                            id="socialUrl"
                            value={localSocialUrl}
                            onChange={e => setLocalSocialUrl(e.target.value)}
                            placeholder={currentSocial.placeholderUrl}
                            className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full pl-10 sm:text-sm border-line rounded-md"
                          />
                        </div>
                        )}
                        <button
                          onClick={handleLocalSave}
                          disabled={isSaving}
                          className="ml-2 inline-flex items-center px-3 py-2 border border-line text-sm leading-4 font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
                        >
                          {isSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Guardar
                            </>
                          )}
                        </button>
                      </div>
                      {selectedSocial !== 'none' && (
                      <p className="mt-1 text-xs text-content-3">
                        {selectedSocial === 'whatsapp'
                          ? 'Pega el enlace wa.me o escribe solo el número con lada país (ej. 5216861234567)'
                          : 'Ingresa la URL completa incluyendo https://'}
                      </p>
                      )}
                    </div>
                  ) : (
                    <div className="mb-4">
                      {saveSuccess && (
                        <div className="mb-2 p-2 bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 text-green-700 dark:text-green-300 text-sm">
                          URL guardada correctamente
                        </div>
                      )}
                      <div className="flex items-center mb-2">
                        <div className={`mr-2 ${currentSocial.iconClass}`}>
                          {currentSocial.icon}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{currentSocial.name}</p>
                          <p className="text-xs text-content-3">
                            {socialUrl ? (
                              <a href={socialUrl} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                                {socialUrl}
                              </a>
                            ) : (
                              <span className="text-content-3 italic">No configurada</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedSocial === 'none' && !socialUrl ? (
                    /* QR apagado: enseñar un QR de lealify.com aquí sería mentirle al dueño. */
                    <div className="flex flex-col items-center justify-center py-8">
                      <X className="h-10 w-10 text-content-3" />
                      <div className="mt-3 font-medium text-content-2">Sin QR al final</div>
                      <p className="text-sm text-content-3 mt-1 text-center">
                        El kiosko cierra tras el feedback sin mostrar ningún código.
                      </p>
                    </div>
                  ) : (
                  <div className="flex flex-col items-center">
                    <div className="p-2 bg-white rounded-xl shadow-sm dark:shadow-none dark:ring-1 dark:ring-white/15">
                      <QRCodeSVG
                        id="qrcode-social"
                        value={socialUrl || lealifyUrl}
                        size={150}
                        bgColor={"#ffffff"}
                        fgColor={currentSocial.color}
                        level={"H"}
                        includeMargin={true}
                      />
                    </div>
                    <div className="mt-4 text-center">
                      <div className={`font-medium ${currentSocial.iconClass}`}>
                        {currentSocial.callToAction}
                      </div>
                      <p className="text-sm text-content-3 mt-1">
                        Escanea este código para acceder directamente
                      </p>
                    </div>
                  </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  {/* Vista previa con overlay para usuarios FREE */}
                  <div className="relative">
                    <div className="opacity-30 p-2 bg-white rounded-xl">
                      <QRCodeSVG
                        id="qrcode-social-preview"
                        value={"https://example.com"} 
                        size={150} 
                        bgColor={"#ffffff"} 
                        fgColor={"#E1306C"}
                        level={"H"}
                        includeMargin={true}
                      />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-surface bg-opacity-80 p-2 rounded-lg text-center">
                        <Crown className="h-8 w-8 text-yellow-500 mx-auto mb-1" />
                        <span className="text-sm font-medium">Función PRO</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 text-center">
                    <div className="font-medium">Personaliza tu QR</div>
                    <p className="text-sm text-content-3 mt-1 mb-4">
                      Dirige a tus clientes a tus propias redes sociales
                    </p>
                    
                    <ProFeatureButton>
                      Personalizar QR
                    </ProFeatureButton>
                  </div>
                  
                  <div className="mt-4 w-full text-xs text-content-3 p-2 bg-yellow-50 dark:bg-yellow-500/10 rounded-lg">
                    <p className="flex items-center">
                      <Crown className="h-3 w-3 mr-1 text-yellow-500" />
                      <span className="font-medium">Beneficios PRO:</span>
                    </p>
                    <ul className="mt-1 pl-6 list-disc">
                      <li>Códigos QR específicos para cada red social</li>
                      <li>Personaliza colores según tu marca</li>
                      <li>Aumenta seguidores en redes sociales</li>
                      <li>Genera más reseñas en Google</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Default Lealify QR (shown for both, but highlighted for FREE) */}
          <div className={`bg-surface shadow rounded-lg overflow-hidden ${!hasProAccess ? 'border-2 border-accent/40' : ''}`}>
            <div className="px-4 py-3 sm:px-6 bg-blue-50 dark:bg-blue-500/10">
              <div className="flex justify-between items-center">
                <h3 className="text-base font-medium text-blue-900 dark:text-blue-200 flex items-center">
                  <Globe className="h-5 w-5 mr-2 text-accent" />
                  Código QR Predeterminado
                  {!hasProAccess && <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">PLAN ACTUAL</span>}
                </h3>
              </div>
            </div>
            <div className="px-4 py-5 sm:p-6 flex flex-col items-center">
              <div className="p-2 bg-white rounded-xl shadow-sm dark:shadow-none dark:ring-1 dark:ring-white/15">
                <QRCodeSVG
                  id="qrcode-lealify"
                  value={lealifyUrl}
                  size={150}
                  bgColor={"#ffffff"}
                  fgColor={"#1a4bff"}
                  level={"H"}
                  includeMargin={true}
                />
              </div>
              <div className="mt-4 text-center">
                <div className="font-medium">Promociona Lealify</div>
                <p className="text-sm text-content-3 mt-1">
                  Lleva a lealify.com: para que quien lo escanee conozca el programa que usas. No registra visitas.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* F1 reorden 24-oct-2026: el «QR en el mostrador» (registro de visitas) ya NO se repite aquí;
            se enciende en un solo lugar: Mi Negocio › Cómo registro visitas. */}
      </div>
    </div>
  );
};

export default IntegratedQRCodePanel;